import { Filter, X } from "lucide-react";
import { useState, useEffect, useMemo } from "react";

const DataTable = ({
    data,
    columns,
    onRowClick,
    searchableColumns = [],
    initialSort = { key: null, direction: "ascending" },
    initialEntriesPerPage = 10,
    isLoading = false,
    filterOptions = [],
    config = {
        showFilter: true,
        showSearch: true,
        showTopEntries: true,
        showBottomEntries: true,
        showPagination: true,
        clickableRows: true,
        entriesOptions: [5, 10, 25, 50, 100],
    },
    tableId,
}) => {
    const storageKey = tableId ? `datatable-state-${tableId}` : null;
    const loadPersistedState = () => {
        if (!storageKey) return {};
        try {
            const persisted = localStorage.getItem(storageKey);
            return persisted ? JSON.parse(persisted) : {};
        } catch {
            return {};
        }
    };

    const persisted = loadPersistedState();

    const [searchTerm, setSearchTerm] = useState(persisted.searchTerm ?? "");
    const [entriesPerPage, setEntriesPerPage] = useState(
        persisted.entriesPerPage ?? initialEntriesPerPage
    );
    const [currentPage, setCurrentPage] = useState(persisted.currentPage ?? 1);
    const [sortConfig, setSortConfig] = useState(
        persisted.sortConfig ?? initialSort
    );
    const [filters, setFilters] = useState(persisted.filters ?? {});
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        if (!storageKey) return;
        // console.log(currentPage)
        // console.log(persisted)
        localStorage.setItem(
            storageKey,
            JSON.stringify({
                searchTerm,
                entriesPerPage,
                currentPage,
                sortConfig,
                filters,
            })
        );
    }, [
        searchTerm,
        entriesPerPage,
        currentPage,
        sortConfig,
        filters,
        storageKey,
    ]);

    const filteredData = useMemo(() => {
        // Debug logs to investigate "Cannot read properties of undefined (reading 'filter')"
        // console.log('DataTable debug:', {
        //     dataType: typeof data,
        //     isArray: Array.isArray(data),
        //     dataLength: Array.isArray(data) ? data.length : undefined,
        //     searchableColumns,
        //     searchTerm
        // });

        if (!Array.isArray(data)) {
            console.error("Data is not an array, skipping filter step", {
                data,
            });
            return [];
        }

        let filtered = data.filter((item) =>
            searchableColumns.some((column) =>
                (item?.[column]?.toString() ?? "")
                    .toLowerCase()
                    .includes((searchTerm ?? "").toLowerCase())
            )
        );

        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                filtered = filtered.filter((item) => {
                    const column = columns.find((col) => col.key === key);
                    const itemValue = column.render
                        ? column.render(item)
                        : item[key];
                    return itemValue === value;
                });
            }
        });
        return filtered;
    }, [data, searchableColumns, searchTerm, filters, columns]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
        const column = columns.find((col) => col.key === sortConfig.key);
        if (!column) return filteredData;
        const sorted = [...filteredData].sort((a, b) => {
            const aValue = column.render ? column.render(a) : a[sortConfig.key];
            const bValue = column.render ? column.render(b) : b[sortConfig.key];
            if (!aValue && !bValue) return 0;
            if (!aValue) return 1;
            if (!bValue) return -1;
            return sortConfig.direction === "ascending"
                ? aValue.toString().localeCompare(bValue.toString())
                : bValue.toString().localeCompare(aValue.toString());
        });
        return sorted;
    }, [filteredData, columns, sortConfig]);

    const totalPages = Math.ceil(sortedData.length / entriesPerPage);
    const indexOfLastItem = config.showPagination
        ? currentPage * entriesPerPage
        : sortedData.length;
    const indexOfFirstItem = config.showPagination
        ? indexOfLastItem - entriesPerPage
        : 0;
    const currentItems = config.showPagination
        ? sortedData.slice(indexOfFirstItem, indexOfLastItem)
        : sortedData;

    // useEffect(() => {
    //     if (currentPage > totalPages) {
    //         setCurrentPage(totalPages === 0 ? 1 : totalPages);
    //     }
    // }, [totalPages, currentPage]);

    const sortData = (key) => {
        let direction = "ascending";
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key, direction });
    };

    const SkeletonRow = () => (
        <tr className="animate-pulse">
            {columns.map((_, index) => (
                <td key={index} className="p-2 md:p-4">
                    <div className="bg-gray-200 rounded-sm h-4"></div>
                </td>
            ))}
        </tr>
    );

    const SkeletonHeader = () => (
        <tr>
            {columns.map((_, index) => (
                <th key={index} className="p-2 md:p-4 min-w-14">
                    <div className="bg-gray-200 rounded-sm w-20 h-4 animate-pulse"></div>
                </th>
            ))}
        </tr>
    );

    const resetFilters = () => {
        setFilters({});
        setSearchTerm("");
        setCurrentPage(1);
        setSortConfig(initialSort);
    };

    const FilterCard = () => (
        <div
            className={`w-full my-2 overflow-hidden transition-all duration-300 ${
                showFilters ? "max-h-96" : "max-h-0"
            }`}
        >
            <div className="flex-col m-0 p-4 border rounded-md card-basic">
                <div className="flex items-center gap-2 mb-4">
                    <h3 className="font-normal text-base">Filter Data</h3>
                    <button
                        onClick={resetFilters}
                        className="btn-danger-outline"
                    >
                        Reset
                    </button>
                </div>
                <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                    {filterOptions.map(({ key, label, options }) => (
                        <div key={key}>
                            <label className="mb-1 font-medium text-gray-700 text-sm">
                                {label}
                            </label>
                            <select
                                className="px-2 py-2 border rounded-full focus:outline-hidden focus:ring-1 focus:ring-primary w-full text-sm"
                                value={filters[key] || ""}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        [key]: e.target.value || undefined,
                                    }))
                                }
                            >
                                <option value="">Semua</option>
                                {options.map((option) => {
                                    // Handle both string arrays and object arrays
                                    const value =
                                        typeof option === "string"
                                            ? option
                                            : option.value;
                                    const label =
                                        typeof option === "string"
                                            ? option
                                            : option.label;
                                    return (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div
                className={`flex flex-col md:flex-row md:justify-between flex-wrap gap-2 ${
                    config.showTopEntries ? "mb-2" : ""
                }`}
            >
                {isLoading ? (
                    <>
                        <div className="flex items-center gap-2">
                            <div className="bg-gray-200 rounded-sm w-32 h-8 animate-pulse"></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="bg-gray-200 rounded-sm w-48 h-8 animate-pulse"></div>
                        </div>
                    </>
                ) : (
                    <>
                        {config.showTopEntries && (
                            <div className="flex items-center gap-2">
                                <span>Tampilkan</span>
                                <select
                                    className="px-2 py-1 border rounded-sm"
                                    value={entriesPerPage}
                                    onChange={(e) =>
                                        setEntriesPerPage(
                                            Number(e.target.value)
                                        )
                                    }
                                >
                                    {config.entriesOptions.map((value) => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </select>
                                <span>item</span>
                            </div>
                        )}
                        <div className="flex md:flex-row flex-col items-start md:items-center gap-2 w-full md:w-auto">
                            {config.showSearch && (
                                <div className="flex items-center gap-2">
                                    <span>Pencarian:</span>
                                    <input
                                        type="text"
                                        className="bg-white shadow-xs px-2 py-1 border rounded-sm focus:outline-hidden hover:ring-1 hover:ring-primary focus:ring-2 focus:ring-primary transition-all duration-300"
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                    />
                                </div>
                            )}
                            {config.showFilter && filterOptions.length > 0 && (
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className="flex items-center gap-1 m-0 btn-mobile-primary"
                                >
                                    <Filter size={16} />
                                    Filter
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
            {config.showFilter && filterOptions.length > 0 && <FilterCard />}
            <div
                className={`bg-white shadow-xs rounded-md overflow-auto text-nowrap ${
                    config.showBottomEntries ? "mb-4" : ""
                }  `}
            >
                <table className="rounded-md w-full">
                    <thead className="border-b">
                        {isLoading ? (
                            <SkeletonHeader />
                        ) : (
                            <tr>
                                {columns.map(
                                    ({ key, label, sortable, headerAlign }) => (
                                        <th
                                            key={key}
                                            onClick={() =>
                                                sortable && sortData(key)
                                            }
                                            className={`min-w-14 p-2 md:p-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                                sortable
                                                    ? "cursor-pointer hover:bg-gray-50"
                                                    : ""
                                            } ${
                                                headerAlign === "center"
                                                    ? "text-center"
                                                    : headerAlign === "right"
                                                    ? "text-right"
                                                    : "text-left"
                                            }`}
                                        >
                                            {label}
                                            {sortable &&
                                                sortConfig.key === key &&
                                                (sortConfig.direction ===
                                                "ascending"
                                                    ? " ↑"
                                                    : " ↓")}
                                        </th>
                                    )
                                )}
                            </tr>
                        )}
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {isLoading ? (
                            Array(5 || entriesPerPage)
                                .fill(0)
                                .map((_, index) => <SkeletonRow key={index} />)
                        ) : (
                            <>
                                {currentItems.map(
                                    (item, index) => (
                                        // console.log(item),
                                        (
                                            <tr
                                                key={item.id || index}
                                                onClick={() =>
                                                    onRowClick &&
                                                    onRowClick(item)
                                                }
                                                className={`${
                                                    config.clickableRows ===
                                                    true
                                                        ? "hover:bg-gray-50 hover:cursor-pointer"
                                                        : ""
                                                } transition-all duration-300 ease-in-out`}
                                            >
                                                {columns.map(
                                                    ({
                                                        key,
                                                        render,
                                                        cellStyle,
                                                        cellAlign,
                                                    }) => (
                                                        <td
                                                            key={key}
                                                            className={`${
                                                                key ===
                                                                "actions"
                                                                    ? "p-1"
                                                                    : "p-2 md:p-4"
                                                            } ${
                                                                cellAlign ===
                                                                "center"
                                                                    ? "text-center"
                                                                    : cellAlign ===
                                                                      "right"
                                                                    ? "text-right"
                                                                    : "text-left"
                                                            }`}
                                                        >
                                                            <span
                                                                className={cellStyle?.(
                                                                    item
                                                                )}
                                                            >
                                                                {render
                                                                    ? render(
                                                                          item
                                                                      )
                                                                    : item[key]}
                                                            </span>
                                                        </td>
                                                    )
                                                )}
                                            </tr>
                                        )
                                    )
                                )}
                                {currentItems.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={columns.length}
                                            className="p-4 text-gray-500 text-center italic"
                                        >
                                            Tidak ada data
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between items-center">
                {isLoading ? (
                    <>
                        <div className="bg-gray-200 rounded-sm w-48 h-4 animate-pulse"></div>
                        <div className="flex gap-2">
                            <div className="bg-gray-200 rounded-sm w-24 h-8 animate-pulse"></div>
                            <div className="bg-gray-200 rounded-sm w-24 h-8 animate-pulse"></div>
                        </div>
                    </>
                ) : (
                    <>
                        {config.showBottomEntries && (
                            <div>
                                {sortedData.length === 0
                                    ? "Menampilkan 0 - 0 dari 0 item"
                                    : `Menampilkan ${
                                          indexOfFirstItem + 1
                                      } - ${Math.min(
                                          indexOfLastItem,
                                          sortedData.length
                                      )} dari ${sortedData.length} item`}
                            </div>
                        )}
                        {config.showPagination && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() =>
                                        setCurrentPage((prev) =>
                                            Math.max(prev - 1, 1)
                                        )
                                    }
                                    disabled={
                                        currentPage === 1 ||
                                        sortedData.length === 0
                                    }
                                    className="btn-primary-outline"
                                >
                                    Sebelumnya
                                </button>
                                <button
                                    onClick={() =>
                                        setCurrentPage((prev) =>
                                            Math.min(prev + 1, totalPages)
                                        )
                                    }
                                    disabled={
                                        currentPage === totalPages ||
                                        sortedData.length === 0
                                    }
                                    className="btn-primary-outline"
                                >
                                    Berikutnya
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default DataTable;
